import { AFV_PERMISSIONS } from '@constants/afv-permissions.constant';
import { PORTAL_PERMISSIONS } from '@constants/portal-permissions.constant';
import { REGISTRATIONS_PERMISSIONS } from '@constants/registrations-permissions.constant';
import type { IOrder } from '@interfaces/order';
import { EUserProfileIds, type IUser } from '@interfaces/user';
import { hasAssociatedUserProfile } from '@utils/client-area-permissions.util';

function isSellerOrAdmin(user?: IUser | null): boolean {
	return (
		user?.profileId === EUserProfileIds.SELLER ||
		user?.profileId === EUserProfileIds.APPLICATION_ADMIN
	);
}

function isApplicationAdmin(user?: IUser | null): boolean {
	return user?.profileId === EUserProfileIds.APPLICATION_ADMIN;
}

function hasPermission(user: IUser | null | undefined, permission: string) {
	return user?.profilePermissions?.includes(permission) ?? false;
}

/**
 * Tenant must have portalOrders.
 * APPLICATION_ADMIN always allowed (Kai); SELLER needs portal.createOrders.
 */
export function canCreatePortalOrders(
	user: IUser | null | undefined,
	options: {
		portalOrdersEnabled: boolean;
	}
): boolean {
	if (!user || !options.portalOrdersEnabled || !isSellerOrAdmin(user)) {
		return false;
	}
	if (isApplicationAdmin(user)) return true;
	return hasPermission(user, PORTAL_PERMISSIONS.createOrders);
}

/**
 * Same base roles + portalOrders.
 * APPLICATION_ADMIN always allowed; SELLER needs registrations.manageClients.
 */
export function canManagePortalClients(
	user: IUser | null | undefined,
	options: {
		portalOrdersEnabled: boolean;
	}
): boolean {
	if (!user || !options.portalOrdersEnabled || !isSellerOrAdmin(user)) {
		return false;
	}
	if (isApplicationAdmin(user)) return true;
	return hasPermission(user, REGISTRATIONS_PERMISSIONS.manageClients);
}

/**
 * Edit a sent order in the portal seller-order flow.
 * Needs portalOrders + status.allowOrderEdit. Profile tenants also need
 * afv.editSentOrders; legacy (no granular profile) follows status only.
 */
export function canEditSentPortalOrder(
	user: IUser | null | undefined,
	order: IOrder | null | undefined,
	options: {
		portalOrdersEnabled: boolean;
		useLegacyPermissions: boolean;
	}
): boolean {
	if (
		!user ||
		!order ||
		!options.portalOrdersEnabled ||
		!isSellerOrAdmin(user)
	) {
		return false;
	}
	if (
		order.cancelledAt ||
		order.status?.isCancellation ||
		order.status?.isFinalization
	) {
		return false;
	}
	if (!order.status?.allowOrderEdit) return false;

	if (options.useLegacyPermissions || !hasAssociatedUserProfile(user)) {
		return true;
	}
	return hasPermission(user, AFV_PERMISSIONS.editSentOrders);
}
